import {
  Alert,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Switch,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { IconAlertCircle } from "@/lib/icons";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/lib/api/client";
import {
  type UserRead,
  updateUser,
} from "@/lib/api/users";

import {
  type EffectiveRoleValue,
  buildRoleOptions,
  effectiveValueFor,
  roleAndSpecialtyFrom,
} from "./roleOptions";

interface Props {
  opened: boolean;
  onClose: () => void;
  user: UserRead | null;
}

export function UserEditDialog({ opened, onClose, user }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const groupedOptions = useMemo(() => buildRoleOptions(t), [t]);

  const form = useForm({
    initialValues: {
      full_name: user?.full_name ?? "",
      effective_role: effectiveValueFor(
        user?.role ?? "",
        user?.specialty ?? "",
      ) as EffectiveRoleValue | "",
      is_active: user?.is_active ?? true,
    },
    validate: {
      full_name: (v) => (v.trim().length > 0 ? null : t("validation.required")),
      effective_role: (v) => (v ? null : t("validation.required")),
    },
  });

  useEffect(() => {
    if (user && opened) {
      form.setValues({
        full_name: user.full_name,
        effective_role: effectiveValueFor(
          user.role,
          user.specialty ?? "",
        ) as EffectiveRoleValue | "",
        is_active: user.is_active,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, opened]);

  const mutation = useMutation({
    mutationFn: (values: typeof form.values) => {
      const { role, specialty } = roleAndSpecialtyFrom(values.effective_role);
      return updateUser(user!.id, {
        full_name: values.full_name.trim(),
        role: role || undefined,
        specialty,
        is_active: values.is_active,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      notifications.show({ color: "green", message: t("users.updated") });
      onClose();
    },
  });

  const errorMessage = (() => {
    if (!mutation.error) return null;
    if (mutation.error instanceof ApiError) return mutation.error.message;
    return t("auth.network_error");
  })();

  if (!user) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("users.edit_title")}
      size="md"
      radius="lg"
    >
      {errorMessage && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} mb="md" radius="lg">
          {errorMessage}
        </Alert>
      )}
      <form onSubmit={form.onSubmit((v) => mutation.mutate(v))}>
        <Stack>
          <TextInput
            label={t("users.fields.full_name")}
            required
            {...form.getInputProps("full_name")}
          />
          <Select
            label={t("users.fields.role")}
            required
            searchable
            data={groupedOptions}
            {...form.getInputProps("effective_role")}
          />
          <Switch
            label={t("users.fields.active")}
            description={t("users.fields.active_hint")}
            {...form.getInputProps("is_active", { type: "checkbox" })}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" color="gray" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={mutation.isPending} radius="xl">
              {t("common.save")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
